import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createBlog } from "./adminApi";

function AdminAddBlog() {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const editorImageInputRef = useRef(null);

  const updateEditorContent = () => {
    setContent(editorRef.current?.innerHTML || "");
  };

  const applyEditorCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    updateEditorContent();
  };

  const setBlockFormat = (format) => {
    editorRef.current?.focus();
    document.execCommand("formatBlock", false, format);
    updateEditorContent();
  };

  const setFontSize = (size) => {
    if (!size) return;
    editorRef.current?.focus();
    document.execCommand("fontSize", false, size);
    updateEditorContent();
  };

  const addLink = () => {
    const url = window.prompt("Enter link URL");
    if (url) {
      applyEditorCommand("createLink", url);
    }
  };

  const insertEditorImage = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      applyEditorCommand("insertImage", reader.result);
      event.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleImageFile = (file) => {
    if (!file || !file.type.startsWith("image/")) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleImageChange = (event) => {
    handleImageFile(event.target.files?.[0]);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleImageFile(event.dataTransfer.files?.[0]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const plainContent = editorRef.current?.innerText.trim() || "";
    if (!plainContent) {
      alert("Please enter blog content.");
      editorRef.current?.focus();
      return;
    }

    if (!imageFile) {
      alert("Please upload a blog image.");
      return;
    }

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content || editorRef.current?.innerHTML || "");
    formData.append("image", imageFile);

    setError("");
    setIsSubmitting(true);

    try {
      await createBlog(formData);
      alert("Blog published successfully.");
      navigate("/admin/view-blogs");
    } catch (error) {
      setError(error.message || "Unable to publish blog.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-page admin-add-blog">
      <form className="add-blog-card" onSubmit={handleSubmit}>
        <div className="add-blog-heading">
          <div>
            <p className="eyebrow">Admin Panel</p>
            <h2>Add Blog</h2>
          </div>
          <span className="add-blog-heading-mark" aria-hidden="true">CMS</span>
        </div>

        <div className="panel-block add-blog-field">
          <label className="field-label" htmlFor="blog-title">
            <span className="field-icon" aria-hidden="true">T</span>
            <span>Blog Title</span>
            <span className="required-pill">Required</span>
          </label>
          <input
            id="blog-title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder="Enter Blog Title"
            required
          />
        </div>

        <div className="panel-block editor-block minimal-editor add-blog-field">
          <label className="field-label">
            <span className="field-icon" aria-hidden="true">C</span>
            <span>Content</span>
            <span className="required-pill">Required</span>
          </label>
          <div className="editor-toolbar" aria-label="Content editor toolbar">
            <button type="button" onClick={() => setBlockFormat("<p>")} aria-label="Paragraph">
              P
            </button>
            <button type="button" onClick={() => setBlockFormat("<h1>")} aria-label="Heading 1">
              H1
            </button>
            <button type="button" onClick={() => setBlockFormat("<h2>")} aria-label="Heading 2">
              H2
            </button>
            <button type="button" onClick={() => setBlockFormat("<h3>")} aria-label="Heading 3">
              H3
            </button>
            <button type="button" onClick={() => setBlockFormat("<blockquote>")} aria-label="Quote">
              ❝
            </button>
            <button type="button" onClick={() => applyEditorCommand("bold")} aria-label="Bold">
              <strong>B</strong>
            </button>
            <button type="button" onClick={() => applyEditorCommand("italic")} aria-label="Italic">
              <em>I</em>
            </button>
            <button type="button" onClick={() => applyEditorCommand("underline")} aria-label="Underline">
              <span className="toolbar-underlined">U</span>
            </button>
            <button
              type="button"
              onClick={() => applyEditorCommand("insertUnorderedList")}
              aria-label="Lists"
            >
              List
            </button>
            <label className="toolbar-select-label">
              Size
              <select onChange={(event) => setFontSize(event.target.value)} defaultValue="">
                <option value="">Auto</option>
                <option value="2">Small</option>
                <option value="3">Normal</option>
                <option value="4">Large</option>
                <option value="5">XL</option>
              </select>
            </label>
            <button type="button" onClick={addLink} aria-label="Link">
              Link
            </button>
            <button
              type="button"
              onClick={() => editorImageInputRef.current?.click()}
              aria-label="Image"
            >
              Img
            </button>
            <input
              ref={editorImageInputRef}
              type="file"
              accept="image/*"
              className="editor-image-input"
              onChange={insertEditorImage}
            />
          </div>
          <div
            ref={editorRef}
            className="editor-body"
            contentEditable
            role="textbox"
            aria-label="Blog content"
            aria-required="true"
            onInput={updateEditorContent}
            data-placeholder="Enter blog content here..."
          />
        </div>

        <div className="panel-block add-blog-field">
          <label className="field-label" htmlFor="blog-image">
            <span className="field-icon" aria-hidden="true">I</span>
            <span>Upload Image</span>
            <span className="required-pill">Required</span>
          </label>
          <div
            className={`image-upload blog-image-dropzone${isDragging ? " is-dragging" : ""}`}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            {imagePreview ? (
              <img src={imagePreview} alt="Uploaded blog preview" />
            ) : (
              <div className="image-empty">
                <span className="upload-icon" aria-hidden="true">IMG</span>
                <strong>Drag & drop image here</strong>
                <span>or click to upload</span>
              </div>
            )}
            <input
              id="blog-image"
              type="file"
              accept="image/*"
              required={!imagePreview}
              onChange={handleImageChange}
            />
          </div>
        </div>

        <div className="add-blog-actions">
          {error && <p className="auth-error">{error}</p>}
          <button
            type="submit"
            className="admin-button primary publish-blog-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Publishing..." : "Publish Blog"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminAddBlog;
